import { useRef } from 'react';
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { EnrollmentStatusResponse, NewOccupantRequest, NewOccupantResponse } from '@rheora/enrollment-contract';

import { getEnrollmentStatus, postNewOccupant } from './status.fetchers';

/**
 * TanStack hooks for the E-SF07 status page. Pages call THESE, never the fetchers.
 * The lifecycle is a query (a read, keyed by the status token); the switch-hold
 * resolution is a mutation (a POST with an idempotency key + a document upload).
 */

/** The enrollment lifecycle for a status token. Disabled until a token is in hand. */
export function useEnrollmentStatusQuery(
  token: string | undefined,
): UseQueryResult<EnrollmentStatusResponse> {
  return useQuery({
    queryKey: ['enrollment-status', token],
    queryFn: () => getEnrollmentStatus(token ?? ''),
    enabled: token !== undefined && token !== '',
    // A signed enrollment moves pending → scheduled → active over days; the page
    // is re-read on mount/refetch, not polled here (the customer reloads the link).
    retry: false,
  });
}

/**
 * How long the confirmation screen keeps asking before it stops and defers to the
 * email. Ten minutes: the signature worker's own stuck-submission alarm fires at
 * ten, so past that point something is wrong and a spinner is no longer honest.
 */
export const AGREEMENT_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const AGREEMENT_POLL_INTERVAL_MS = 3_000;

/**
 * Poll an enrollment until its signed contract exists (E-SF06).
 *
 * SEPARATE FROM `useEnrollmentStatusQuery` ON PURPOSE. That one is deliberately
 * not polled — the status page is a durable link a customer may open days later,
 * and putting it on an interval would leave every stale tab hitting the BFF for
 * ever. This hook serves the ONE screen where a document is actively being
 * produced and the customer is waiting on it.
 *
 * It stops itself in both directions: the moment `agreementAvailable` turns true,
 * and again at {@link AGREEMENT_POLL_TIMEOUT_MS} whatever the answer. A poll with
 * no ceiling is a page that spins for ever when the worker is wedged — which
 * tells the customer nothing and hides the failure from us.
 */
export function useAgreementReadinessQuery(
  token: string | undefined,
  now: () => number = Date.now,
): UseQueryResult<EnrollmentStatusResponse> {
  const startedAtRef = useRef<number>(now());
  return useQuery({
    queryKey: ['enrollment-agreement-readiness', token],
    queryFn: () => getEnrollmentStatus(token ?? ''),
    enabled: token !== undefined && token !== '',
    refetchInterval: (query) => {
      if (query.state.data?.agreementAvailable === true) return false;
      if (now() - startedAtRef.current >= AGREEMENT_POLL_TIMEOUT_MS) return false;
      return AGREEMENT_POLL_INTERVAL_MS;
    },
    // A transient failure mid-wait must not end it — the next tick asks again.
    // `retry: false` keeps one failure from fanning out into a burst of retries
    // on top of the interval that is already running.
    retry: false,
  });
}

/** Submit the switch-hold resolution (New Occupant Statement + proof) for a token. */
export function useNewOccupantMutation(
  token: string | undefined,
): UseMutationResult<NewOccupantResponse, Error, NewOccupantRequest> {
  return useMutation({
    mutationFn: (request: NewOccupantRequest) => postNewOccupant(token ?? '', request),
  });
}
